import { createContext, useContext, useState, useCallback, useEffect, ReactNode, useRef } from 'react';

import { ChatMessage, Conversation, MessageRole, SocketInputEvents, SocketOutputEvents } from '@/types/socket';
import { useStream } from '@/hooks/useStream';
import { copyToClipboard } from '@/helper/copyToClipboard';
import { captureEvent } from '@/helper/postHog';

type ChatConfig = {
  config: Conversation;
  defaultPrompts?: ChatMessage[];
  inputEvent?: SocketInputEvents;
  outputEvent?: SocketOutputEvents;
  onStreamStart?: () => void;
  onStreamEnd?: () => void;
};

type ChatContextType = {
  message: string;
  isStreaming: boolean;
  messages: ChatMessage[];
  onSendMessage: (customMessage?: string) => void;
  setMessage: (value: string) => void;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  onCopyChat: () => void;
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const speakerMap = {
  assistant: 'Tivy',
  user: 'User'
} as const;

export const ChatProvider = ({
  children,
  config,
  defaultPrompts = [],
  inputEvent,
  outputEvent,
  onStreamStart = undefined,
  onStreamEnd = undefined
}: ChatConfig & { children: ReactNode }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([...config.messages, ...defaultPrompts]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const { isStreaming, streamingMessage, send } = useStream({ inputEvent, outputEvent, onStreamStart, onStreamEnd });

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    requestAnimationFrame(() => {
      chatContainerRef.current?.scrollTo({ top: chatContainerRef.current?.scrollHeight, behavior });
    });
  }, []);

  const onSendMessage = useCallback(
    (customMessage?: string) => {
      if ((!message.trim() && !customMessage) || isStreaming) return;

      const messageContent = customMessage || message;
      const firstUserMessage = messages.find(message => message.role === MessageRole.USER);
      const newMessage: ChatMessage = {
        role: MessageRole.USER,
        content: messageContent
      };

      setMessage('');
      setMessages(prev => [...prev, newMessage]);
      scrollToBottom();

      send({
        ...config,
        title: config?.title || firstUserMessage?.content || messageContent,
        messages: [...messages, newMessage]
      });
    },
    [message, messages, isStreaming, send, config, scrollToBottom]
  );

  const onCopyChat = () => {
    const formattedMessages = messages
      .map((entry: ChatMessage) => {
        if (entry.content.startsWith('Ready to take action?')) return;
        return `${speakerMap[entry.role]}: ${entry.content}`;
      })
      .join('\n');

    copyToClipboard(formattedMessages, () => captureEvent('Chat copied to clipboard'));
  };

  useEffect(() => {
    if (!streamingMessage) return;

    setMessages(prev => {
      const lastMessage = prev[prev.length - 1];

      if (lastMessage?.role === MessageRole.ASSISTANT) {
        const updatedMessages = [...prev];
        updatedMessages[updatedMessages.length - 1] = {
          role: MessageRole.ASSISTANT,
          content: lastMessage.content + streamingMessage.content
        };
        return updatedMessages;
      }

      return [...prev, { role: MessageRole.ASSISTANT, content: streamingMessage.content }];
    });
    scrollToBottom();
  }, [streamingMessage, scrollToBottom]);

  return (
    <ChatContext.Provider
      value={{
        message,
        messages,
        setMessage,
        onCopyChat,
        isStreaming,
        onSendMessage,
        scrollToBottom,
        chatContainerRef
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
